import { UserRole } from "../services/UserService";

/**
 * Formats a role for display in the UI
 * @param role - UserRole enum or string role value
 * @returns Formatted role string (e.g., "Super Admin", "Owner", "Admin", "Member")
 */
export const formatRole = (role: UserRole | string): string => {
  const roleStr = typeof role === "string" ? role : role;
  const normalized = roleStr.toLowerCase();
  if (normalized === "superadmin") {
    return "Super Admin";
  }
  return roleStr.charAt(0).toUpperCase() + roleStr.slice(1).toLowerCase();
};

/**
 * Validates if a string is a valid UserRole
 * @param role - String to validate
 * @returns True if the string is a valid UserRole
 */
export const isValidRole = (role: string): role is UserRole => {
  return Object.values(UserRole).includes(role.toLowerCase() as UserRole);
};

/**
 * Converts a string to UserRole enum, with fallback
 * @param role - String role value from API or UserRole enum
 * @param fallback - Fallback role if conversion fails (defaults to Member)
 * @returns UserRole enum value
 */
export const toUserRole = (role: string | UserRole, fallback: UserRole = UserRole.Member): UserRole => {
  // If already a UserRole enum value, return it
  if (Object.values(UserRole).includes(role as UserRole)) {
    return role as UserRole;
  }
  
  // Convert string to UserRole
  const normalized = role.toLowerCase();
  const roleMap: Record<string, UserRole> = {
    superadmin: UserRole.SuperAdmin,
    owner: UserRole.Owner,
    admin: UserRole.Admin,
    member: UserRole.Member,
  };
  
  return roleMap[normalized] || fallback;
};

/**
 * Type guard to check if a value is a UserRole
 * @param value - Value to check
 * @returns True if value is a UserRole
 */
export const isUserRole = (value: unknown): value is UserRole => {
  return typeof value === "string" && Object.values(UserRole).includes(value as UserRole);
};

/**
 * Gets role hierarchy level (higher number = more permissions)
 * @param role - UserRole to check
 * @returns Level number (4 = SuperAdmin, 3 = Owner, 2 = Admin, 1 = Member)
 */
export const getRoleLevel = (role: UserRole): number => {
  const levels: Record<UserRole, number> = {
    [UserRole.SuperAdmin]: 4,
    [UserRole.Owner]: 3,
    [UserRole.Admin]: 2,
    [UserRole.Member]: 1,
  };
  return levels[role] || 1;
};

/**
 * Compares two roles to determine if role1 has higher or equal permissions than role2
 * @param role1 - First role to compare
 * @param role2 - Second role to compare
 * @returns True if role1 has higher or equal permissions
 */
export const hasRoleOrHigher = (role1: UserRole, role2: UserRole): boolean => {
  return getRoleLevel(role1) >= getRoleLevel(role2);
};

/**
 * Checks if a role can perform admin actions
 * @param role - UserRole to check
 * @returns True if role is SuperAdmin, Owner, or Admin
 */
export const isAdminRole = (role: UserRole): boolean => {
  return role === UserRole.SuperAdmin || role === UserRole.Owner || role === UserRole.Admin;
};

/**
 * Checks if a role is privileged (SuperAdmin or Owner)
 * @param role - UserRole to check
 * @returns True if role is SuperAdmin or Owner
 */
export const isPrivilegedRole = (role: UserRole): boolean => {
  return role === UserRole.SuperAdmin || role === UserRole.Owner;
};
